import React, { useState } from 'react';

const PreferencesForm = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState({
    categories: [],
    priceRanges: [],
    styles: [],
    occasions: [],
    relationships: []
  });

  const categoryOptions = [
    { id: 'electronics', label: 'Electronics & Gadgets', icon: '📱' },
    { id: 'books', label: 'Books & Reading', icon: '📚' },
    { id: 'fashion', label: 'Fashion & Accessories', icon: '👗' },
    { id: 'home', label: 'Home & Garden', icon: '🏠' },
    { id: 'sports', label: 'Sports & Fitness', icon: '⚽' },
    { id: 'art', label: 'Art & Crafts', icon: '🎨' },
    { id: 'music', label: 'Music & Audio', icon: '🎵' },
    { id: 'food', label: 'Food & Cooking', icon: '🍳' },
    { id: 'travel', label: 'Travel & Adventure', icon: '✈️' },
    { id: 'games', label: 'Games & Toys', icon: '🎮' },
    { id: 'beauty', label: 'Beauty & Personal Care', icon: '💄' },
    { id: 'automotive', label: 'Automotive', icon: '🚗' }
  ];

  const priceRangeOptions = [
    { id: 'under25', label: 'Under $25', icon: '💵' },
    { id: '25-50', label: '$25 - $50', icon: '💴' },
    { id: '50-100', label: '$50 - $100', icon: '💶' },
    { id: '100-200', label: '$100 - $200', icon: '💷' },
    { id: 'over200', label: 'Over $200', icon: '💸' }
  ];

  const styleOptions = [
    { id: 'practical', label: 'Practical & Useful', icon: '🔧' },
    { id: 'luxury', label: 'Luxury & Premium', icon: '💎' },
    { id: 'quirky', label: 'Quirky & Fun', icon: '🎭' },
    { id: 'minimalist', label: 'Minimalist & Simple', icon: '⚪' },
    { id: 'vintage', label: 'Vintage & Classic', icon: '🕰️' },
    { id: 'trendy', label: 'Trendy & Modern', icon: '✨' }
  ];

  const occasionOptions = [
    { id: 'birthday', label: 'Birthdays', icon: '🎂' },
    { id: 'holiday', label: 'Holidays', icon: '🎄' },
    { id: 'anniversary', label: 'Anniversaries', icon: '💕' },
    { id: 'graduation', label: 'Graduations', icon: '🎓' },
    { id: 'wedding', label: 'Weddings', icon: '💒' },
    { id: 'justbecause', label: 'Just Because', icon: '💝' }
  ];

  const relationshipOptions = [
    { id: 'family', label: 'Family Members', icon: '👨‍👩‍👧‍👦' },
    { id: 'friends', label: 'Close Friends', icon: '👫' },
    { id: 'colleagues', label: 'Colleagues', icon: '💼' },
    { id: 'romantic', label: 'Romantic Partner', icon: '💑' },
    { id: 'acquaintances', label: 'Acquaintances', icon: '👋' }
  ];

  const handleOptionToggle = (category, optionId) => {
    setPreferences(prev => ({
      ...prev,
      [category]: prev[category].includes(optionId)
        ? prev[category].filter(id => id !== optionId)
        : [...prev[category], optionId]
    }));
  };

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Convert preferences to the format expected by the backend
    const formattedPreferences = [];
    
    Object.entries(preferences).forEach(([category, values]) => {
      values.forEach(value => {
        formattedPreferences.push({
          category,
          preference_type: 'selection',
          preference_value: value,
          weight: 1.0
        });
      });
    });

    onComplete(formattedPreferences);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            title="What categories interest you?"
            subtitle="Select all that apply - this helps us understand your general preferences"
            options={categoryOptions}
            selectedOptions={preferences.categories}
            onToggle={(optionId) => handleOptionToggle('categories', optionId)}
          />
        );
      case 2:
        return (
          <StepContent
            title="What's your typical budget range?"
            subtitle="Select the price ranges you're comfortable with for gifts"
            options={priceRangeOptions}
            selectedOptions={preferences.priceRanges}
            onToggle={(optionId) => handleOptionToggle('priceRanges', optionId)}
          />
        );
      case 3:
        return (
          <StepContent
            title="What's your gift-giving style?"
            subtitle="This helps us match your personality to gift recommendations"
            options={styleOptions}
            selectedOptions={preferences.styles}
            onToggle={(optionId) => handleOptionToggle('styles', optionId)}
          />
        );
      case 4:
        return (
          <StepContent
            title="What occasions do you shop for?"
            subtitle="Select the events you typically buy gifts for"
            options={occasionOptions}
            selectedOptions={preferences.occasions}
            onToggle={(optionId) => handleOptionToggle('occasions', optionId)}
          />
        );
      case 5:
        return (
          <StepContent
            title="Who do you usually shop for?"
            subtitle="This helps us tailor recommendations for different relationships"
            options={relationshipOptions}
            selectedOptions={preferences.relationships}
            onToggle={(optionId) => handleOptionToggle('relationships', optionId)}
          />
        );
      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return preferences.categories.length > 0;
      case 2: return preferences.priceRanges.length > 0;
      case 3: return preferences.styles.length > 0;
      case 4: return preferences.occasions.length > 0;
      case 5: return preferences.relationships.length > 0;
      default: return false;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Step {currentStep} of 5</span>
          <span className="text-sm text-gray-500">{Math.round((currentStep / 5) * 100)}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 5) * 100}%` }}
          ></div>
        </div>
      </div>

      {renderStep()}

      {/* Navigation buttons */}
      <div className="flex justify-between mt-8">
        <button
          onClick={handleBack}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        
        <button
          onClick={handleNext}
          disabled={!canProceed()}
          className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {currentStep === 5 ? 'Complete Setup' : 'Next'}
        </button>
      </div>
    </div>
  );
};

const StepContent = ({ title, subtitle, options, selectedOptions, onToggle }) => (
  <div>
    <div className="text-center mb-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{subtitle}</p>
    </div>
    
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {options.map((option) => (
        <button
          key={option.id}
          onClick={() => onToggle(option.id)}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            selectedOptions.includes(option.id)
              ? 'border-purple-500 bg-purple-50 text-purple-700'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="text-2xl mb-2">{option.icon}</div>
          <div className="font-medium">{option.label}</div>
        </button>
      ))}
    </div>
  </div>
);

export default PreferencesForm;
